import { LazyLoadEvent, ScrollerOptions } from 'primeng/api';
import { Dropdown } from 'primeng/dropdown';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  merge,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';

export interface SmartPickerPage<T extends { id: string }> {
  count: number;
  items: Array<T>;
  limit: number;
  offset: number;
}

// the PrimeNg built-in filtering is broken if used with paginated remote virtual scroll
// (due to the fact that it perform an in-memory filtering).
type FilterOptions = {
  enabled: boolean;
  filterPlaceholder?: string;
  emptyFilterMessage?: string;
};

// enabled: items are fetched immediately (Use this option if the user is likely to open the drop-down menu)
// disabled: items are fetched when the first open event is triggered
export interface SmartPickerPreloadStrategy {
  enabled: boolean;
  autoSelectFirstItem?: boolean;
  autoSelectSingleChoice?: boolean;
}

export interface SmartPickerOptions {
  virtualScroll: boolean;
  filter: FilterOptions;
  initialFetchedItems: number;

  preloadStrategy: SmartPickerPreloadStrategy;

  templateOptions: {
    itemSize: number;
    scrollHeight: number;
  };

  showClear: boolean;
}

export const defaultSmartPickerOptions: SmartPickerOptions = {
  virtualScroll: true,
  filter: {
    enabled: true,
  },
  initialFetchedItems: 20,
  preloadStrategy: {
    enabled: false,
  },
  templateOptions: {
    itemSize: 40,
    scrollHeight: 200,
  },
  showClear: false,
};

export abstract class SmartPickerHandler<T extends { id: string }> {
  readonly options: SmartPickerOptions;
  readonly virtualScrollerOptions?: ScrollerOptions;

  private _dropDown?: Dropdown;
  private _items?: T[];
  private _excludeIds: string[] = [];
  private readonly _filterQuery$ = new BehaviorSubject<string>('');
  private readonly _lazyLoadEvent$ = new BehaviorSubject<LazyLoadEvent | undefined>(undefined);
  private readonly _processing$ = new BehaviorSubject<boolean>(false);
  private readonly _reset$ = new Subject<void>();
  private readonly _opened$ = new Subject<boolean>(); // if the dropdown opened at least one time
  private readonly _selectedId$ = new BehaviorSubject<string | undefined>(undefined);
  private readonly _singleChoice$ = new BehaviorSubject<T | false | null>(null);
  private readonly _firstChoice$ = new BehaviorSubject<T | false | null>(null);
  private readonly _isEmpty$ = new BehaviorSubject<false | null>(null);

  readonly items$: Observable<T[] | null>;
  readonly processing$ = this._processing$.asObservable();

  private get isInitialized() {
    return this._items !== undefined;
  }

  private get excludeIdsParam() {
    return [...this._excludeIds, ...(this._selectedId$.value ? [this._selectedId$.value] : [])];
  }

  constructor(options: SmartPickerOptions) {
    this.virtualScrollerOptions = options.virtualScroll
      ? {
        // Delay combined with showLoader is sometimes buggy
        delay: 0,
        showLoader: false,
        lazy: true,
        onLazyLoad: this.onLazyLoad.bind(this),
      }
      : undefined;

    this.options = options;

    const itemSrc = combineLatest([
      this._filterQuery$.pipe(distinctUntilChanged()),
      merge(this._reset$, of(undefined)).pipe(
        switchMap(() =>
          of(this.options.preloadStrategy.enabled).pipe(
            switchMap((preload) => {
              return !preload ? this._opened$.pipe(filter((opened) => opened)) : of(true);
            }),
            distinctUntilChanged(),
          ),
        ),
      ),
    ]).pipe(
      switchMap(([query]) => {
        this._processing$.next(true);
        return this.getItems({
          offset: 0,
          limit: this.options.initialFetchedItems,
          query: query == '' ? undefined : query,
          excludeIds: this.excludeIdsParam,
        }).pipe(
          switchMap((list) => {
            this._processing$.next(false);
            const count = list.count;
            this._items = new Array(count).fill({
              id: '',
              name: '',
              avatar: '',
            });

            this._items.splice(0, list.items.length, ...list.items);

            if (this.options.preloadStrategy.enabled) {
              // setup singleChoice and firstChoice
              if (this._singleChoice$.value === null) {
                this._singleChoice$.next(count === 1 ? this._items[0] : false);
              }
              if (this._firstChoice$.value === null) {
                this._firstChoice$.next(this._items[0]);
              }
            }

            return this._lazyLoadEvent$.pipe(
              switchMap(async (evt) => {
                return evt ? this.fetchMore(this._items as T[], query, evt) : (this._items as T[]);
              }),
              map((arr) => [...arr]),
            );
          }),
        );
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.items$ = merge(
      itemSrc,
      // emit null array on reset in order to always open a dropdown in a clean state
      // (this fix initial scroll offset issues after the dropdown is reopened)
      this._reset$.pipe(map(() => null)),
    );
  }

  /**
   * A valid handler MUST implement the query and excludeId filters.
   * @param params
   */
  abstract getItems(params: {
    offset: number;
    limit: number;
    query: string | undefined;
    excludeIds: string[] | undefined;
  }): Observable<SmartPickerPage<T>>;

  open() {
    this._opened$.next(true);
  }

  search(query: string) {
    this._filterQuery$.next(query);
  }

  reset(force?: boolean) {
    // reset only when the list is initialized
    if (force || this.isInitialized) {
      this._reset$.next();
    }
  }

  setSelectedId(id: string | undefined) {
    this._selectedId$.next(id);
  }

  setDropdown(dropdown: Dropdown) {
    // dropdown is mainly registered to ignore unneded request
    //  when the dropdown is closed and the eager flag is set to true
    this._dropDown = dropdown;
  }

  setExcludeIds(excludeIds: string[]) {
    this._excludeIds = excludeIds;
    this.reset(true);
  }

  getExcludeIds() {
    return this._excludeIds;
  }

  async getSingleChoice(): Promise<T | undefined> {
    return firstValueFrom(
      this._singleChoice$.pipe(
        filter((val): val is T | false => val !== null),
        map((val) => (val === false ? undefined : val)),
      ),
    );
  }

  async getFirstChoice(): Promise<T | undefined> {
    return firstValueFrom(
      this._firstChoice$.pipe(
        filter((val): val is T | false => val !== null),
        map((val) => (val === false ? undefined : val)),
      ),
    );
  }

  private onLazyLoad(event: LazyLoadEvent) {
    this._lazyLoadEvent$.next(event);
  }

  private async fetchMore(items: T[], query: string, event: LazyLoadEvent) {
    if (this._dropDown && !this._dropDown.overlayVisible) {
      return items;
    }

    const { first, last } = event;

    if (first !== undefined && last !== undefined && first < last) {
      // already loaded
      if (!items.find((i, index) => i.id === '' && index >= first && index <= last)) {
        return items;
      }

      const getNextData = await firstValueFrom(
        this.getItems({
          offset: first,
          limit: last - first,
          query,
          excludeIds: this.excludeIdsParam,
        }),
      );
      const startIndex = first;
      const newItems: T[] = getNextData.items;
      // Ensure the array is large enough to accommodate new items
      if (items.length < startIndex + newItems.length) {
        const additionalItems = new Array(startIndex + newItems.length - items.length).fill({
          id: '',
          name: '',
          avatar: '',
        });
        items.push(...additionalItems);
      }

      // Replace existing elements with new ones
      items.splice(startIndex, newItems.length, ...newItems);

      return items;
    }

    return items;
  }
}
